/* WP-10 login (§14.5.6): passkey primary, OTP fallback; compact task
 * surface (R5) — no decorative hero slot unless the published appearance
 * bundle supplies one; persistent help/legal/language entry points. */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import { useSession } from '../auth/session-context';
import { passkeySignIn, passkeySupported, toPasskeyError } from '../auth/passkey';
import { PrimaryButton, TextLink } from '../components/primitives';
import { mediaUrl, useAppearance } from '../appearance/appearance';
import { errorText } from '../utils/error-text';
import { useResendCooldown } from '../utils/use-resend-cooldown';
import { safeReturnPath } from '../utils/return-path';
import { PORTAL_LOCALES, currentPortalLocale, setPortalLocale } from '../i18n';
import { KeyIcon } from '../icons';

type Mode = 'choose' | 'otp';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const { refresh } = useSession();

  const [mode, setMode] = useState<Mode>('choose');
  const [memberCode, setMemberCode] = useState('');
  const [loginTicket, setLoginTicket] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [channelHint, setChannelHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { secondsLeft: resendSeconds, startCooldown } = useResendCooldown();

  const appearance = useAppearance();
  const heroUrl = mediaUrl(appearance?.document?.identity?.loginBackgroundAssetId);

  const finish = async () => {
    await refresh();
    navigate(safeReturnPath(next), { replace: true });
  };

  const onPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      await passkeySignIn();
      await finish();
    } catch (err) {
      setError(errorText(toPasskeyError(err), t));
    } finally {
      setBusy(false);
    }
  };

  const onSendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await portalApi.post<{
        loginTicket: string;
        channelHint?: string;
        otpExpiresAt: string;
      }>('/auth/login/start', { memberCode: memberCode.trim() });
      setLoginTicket(res.loginTicket);
      setChannelHint(res.channelHint ?? null);
      startCooldown();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await portalApi.post('/auth/login/verify', { loginTicket, otp: otp.trim() });
      await finish();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hp-auth">
      {heroUrl ? (
        <div className="hp-auth__hero" aria-hidden>
          <img src={heroUrl} alt="" />
        </div>
      ) : null}
      <div className="hp-auth__body">
        <div>
          <p className="hp-label">{t('common.appWordmark')}</p>
          <h1 className="hp-display-xl">{t('auth.login.title')}</h1>
          <p className="hp-muted">{t('auth.login.subtitle')}</p>
        </div>

        {mode === 'choose' ? (
          <>
            <PrimaryButton
              onClick={onPasskey}
              loading={busy}
              icon={<KeyIcon size={18} />}
              data-quality-id="portal.login.passkey"
            >
              {passkeySupported() ? t('auth.login.passkey') : t('auth.login.passkeyGeneric')}
            </PrimaryButton>
            <TextLink
              onClick={() => setMode('otp')}
              chevron={false}
              data-quality-id="portal.login.otpLink"
            >
              {t('auth.login.otpLink')}
            </TextLink>
          </>
        ) : !loginTicket ? (
          <form onSubmit={onSendCode} className="hp-stack">
            <label className="hp-field">
              <span className="hp-field__label">{t('auth.login.memberCodeLabel')}</span>
              <input
                className="hp-input"
                value={memberCode}
                onChange={(e) => setMemberCode(e.target.value)}
                autoComplete="username"
                required
                data-quality-id="portal.login.memberCode"
              />
              <span className="hp-field__hint">{t('auth.login.memberCodeHint')}</span>
            </label>
            <PrimaryButton loading={busy} type="submit" data-quality-id="portal.login.sendCode">
              {t('auth.login.sendCode')}
            </PrimaryButton>
          </form>
        ) : (
          <form onSubmit={onVerify} className="hp-stack">
            <p className="hp-muted">
              {t('auth.login.codeSent')}
              {channelHint ? ` — ${t(`common.${channelHint.toLowerCase()}`)}` : ''}
            </p>
            <label className="hp-field">
              <span className="hp-field__label">{t('auth.login.codeLabel')}</span>
              <input
                className="hp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                required
                data-quality-id="portal.login.otp"
              />
              <span className="hp-field__hint">{t('auth.login.codeHint')}</span>
            </label>
            <PrimaryButton loading={busy} type="submit" data-quality-id="portal.login.verify">
              {t('auth.login.verify')}
            </PrimaryButton>
            <div className="hp-row">
              <TextLink
                chevron={false}
                disabled={busy || resendSeconds > 0}
                onClick={() => void onSendCode()}
                data-quality-id="portal.login.resend"
              >
                {resendSeconds > 0
                  ? t('auth.login.resendIn', { seconds: resendSeconds })
                  : t('auth.login.resend')}
              </TextLink>
              <TextLink
                chevron={false}
                tone="muted"
                disabled={busy}
                onClick={() => {
                  setLoginTicket(null);
                  setOtp('');
                }}
                data-quality-id="portal.login.changeCode"
              >
                {t('auth.login.changeCode')}
              </TextLink>
            </div>
          </form>
        )}

        {error ? (
          <p className="hp-danger-text" role="alert">
            {error}
          </p>
        ) : null}

        <div className="hp-auth__foot">
          <Link to="/help">{t('me.help')}</Link>
          <Link to="/legal/terms">{t('legal.terms')}</Link>
          <Link to="/legal/privacy">{t('legal.privacy')}</Link>
          <span className="hp-auth__lang">
            {PORTAL_LOCALES.map((loc) => (
              <button
                key={loc}
                type="button"
                aria-pressed={currentPortalLocale() === loc}
                onClick={() => setPortalLocale(loc)}
              >
                {t(`me.languageNames.${loc}`)}
              </button>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}
